export interface ReplyEffect {
  readonly feature: string;
  readonly level: string;
  readonly reference: string;
  readonly oddsRatio: number; // exp(coefficient), 3 decimals
  readonly low: number; // odds ratio, 3 decimals
  readonly high: number;
  readonly trainN: number;
}

export interface ReplySegment {
  readonly feature: string;
  readonly level: string;
  readonly n: number;
  readonly replied: number;
  readonly shrunkRate: number; // 4 decimals
}

export interface ReplyEvidence {
  readonly version: "reply-model-v1";
  readonly computedOn: string; // "2026-10-18"
  readonly corpusSha256Prefix: string; // 16 hex chars
  readonly clearEffects: readonly ReplyEffect[]; // only effects with clear === true
  readonly segments: readonly ReplySegment[];
}

/** Freezes the array and each element in it, so no effect or segment can be rewritten in place. */
const frozenAll = <T extends object>(xs: readonly T[]): readonly T[] => Object.freeze(xs.map((x) => Object.freeze(x)));

/**
 * TA-1360 learning feed (spec 2026-10-17 section 4). What the reply-chance
 * model measured on a shop's own past chats, as aggregates: the clear effects
 * and the shrunken reply rates by segment. Keyed by client id (CLAUDE.md 0.6):
 * a shop with no entry gets nothing, never another shop's numbers. Pinned to
 * the committed model file (replyEvidence.test.ts); a refit regenerates it.
 * Associations, not causes: these seed hypotheses and owner-page counts, they
 * never change a reply on their own.
 */
export const REPLY_EVIDENCE_BY_CLIENT: Readonly<Record<string, ReplyEvidence>> = Object.freeze({
  cl_nekta: Object.freeze({
    version: "reply-model-v1" as const,
    computedOn: "2026-10-18",
    corpusSha256Prefix: "4b2f80dae9b039d7",
    clearEffects: frozenAll([
      { feature: "closer", level: "question", reference: "none", oddsRatio: 2.983, low: 2.376, high: 3.731, trainN: 575 },
      { feature: "first", level: "yes", reference: "no", oddsRatio: 1.164, low: 1.001, high: 1.374, trainN: 914 },
      { feature: "inquiry", level: "price_check", reference: "custom", oddsRatio: 0.349, low: 0.259, high: 0.458, trainN: 462 },
      { feature: "inquiry", level: "ready_made", reference: "custom", oddsRatio: 0.735, low: 0.572, high: 0.89, trainN: 1087 },
      { feature: "inquiry", level: "unlabelled", reference: "custom", oddsRatio: 0.466, low: 0.333, high: 0.626, trainN: 337 },
      { feature: "length", level: "41-100", reference: "<=40", oddsRatio: 1.249, low: 1.021, high: 1.54, trainN: 1061 },
      { feature: "opener", level: "greeting+name", reference: "direct", oddsRatio: 0.517, low: 0.312, high: 0.878, trainN: 189 },
      { feature: "response", level: "1-4h", reference: "<=2m", oddsRatio: 0.337, low: 0.271, high: 0.418, trainN: 444 },
      { feature: "response", level: "10-60m", reference: "<=2m", oddsRatio: 0.406, low: 0.284, high: 0.609, trainN: 119 },
      { feature: "response", level: "4-24h", reference: "<=2m", oddsRatio: 0.272, low: 0.218, high: 0.327, trainN: 763 },
      { feature: "response", level: ">24h", reference: "<=2m", oddsRatio: 0.21, low: 0.172, high: 0.252, trainN: 807 },
      { feature: "warm", level: "yes", reference: "no", oddsRatio: 0.434, low: 0.297, high: 0.621, trainN: 273 },
    ]),
    segments: frozenAll([
      { feature: "inquiry", level: "custom", n: 1909, replied: 1390, shrunkRate: 0.727 },
      { feature: "inquiry", level: "ready_made", n: 1226, replied: 782, shrunkRate: 0.638 },
      { feature: "inquiry", level: "price_check", n: 523, replied: 245, shrunkRate: 0.4771 },
      { feature: "inquiry", level: "unlabelled", n: 511, replied: 271, shrunkRate: 0.5361 },
      { feature: "inquiry", level: "repair", n: 96, replied: 63, shrunkRate: 0.6538 },
      { feature: "inquiry", level: "warranty", n: 1, replied: 1, shrunkRate: 0.6578 },
      { feature: "channel", level: "dm", n: 4266, replied: 2752, shrunkRate: 0.6451 },
      { feature: "closer", level: "none", n: 3102, replied: 1908, shrunkRate: 0.6155 },
      { feature: "closer", level: "question", n: 666, replied: 544, shrunkRate: 0.807 },
      { feature: "closer", level: "period", n: 419, replied: 248, shrunkRate: 0.5966 },
      { feature: "closer", level: "exclamation", n: 43, replied: 33, shrunkRate: 0.7082 },
      { feature: "closer", level: "signoff", n: 36, replied: 19, shrunkRate: 0.5898 },
      { feature: "response", level: "<=2m", n: 1650, replied: 1288, shrunkRate: 0.7795 },
      { feature: "response", level: ">24h", n: 991, replied: 474, shrunkRate: 0.4805 },
      { feature: "response", level: "4-24h", n: 830, replied: 469, shrunkRate: 0.5663 },
      { feature: "response", level: "1-4h", n: 484, replied: 286, shrunkRate: 0.5924 },
      { feature: "response", level: "no customer message before", n: 182, replied: 147, shrunkRate: 0.7966 },
      { feature: "response", level: "10-60m", n: 129, replied: 88, shrunkRate: 0.6787 },
      { feature: "asked", level: "no", n: 3292, replied: 2013, shrunkRate: 0.6121 },
      { feature: "asked", level: "yes", n: 974, replied: 739, shrunkRate: 0.7521 },
    ]),
  }),
});

export function replyEvidenceFor(clientId: string): ReplyEvidence | null {
  return Object.prototype.hasOwnProperty.call(REPLY_EVIDENCE_BY_CLIENT, clientId)
    ? REPLY_EVIDENCE_BY_CLIENT[clientId]!
    : null;
}
